import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, InlineLoading } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import classNames from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import { useHotkeys } from 'react-hotkeys-hook';
import {
  closeExportImageAtom,
  exportImageStateAtom,
  openExportImageAtom,
} from './atoms';
import { buildExportFilename } from './utils';
import {
  ExportImageOptionsFields,
  isCustomDimensionValid,
} from './export-image-options-fields';
import type {
  ChartImageExporter,
  ElementDescriptor,
  ExportImageOptions,
  OutputFormat,
} from './types';
import style from './export-image-drawer.module.scss';

export function useOpenExportImage() {
  const open = useSetAtom(openExportImageAtom);
  return useCallback(
    (payload: {
      exporter: ChartImageExporter;
      chartLabel: string;
      filenamePrefix: string;
      supportedFormats?: OutputFormat[];
    }) => open(payload),
    [open]
  );
}

interface PanelProps {
  exporter: ChartImageExporter;
  chartLabel: string;
  filenamePrefix: string;
  supportedFormats: OutputFormat[];
  onClose: () => void;
}

// Exported so the in-section push drawer (ExportableHighchart) can reuse the
// exact same export UI/flow.
export const Panel = ({
  exporter,
  chartLabel,
  filenamePrefix,
  supportedFormats,
  onClose,
}: PanelProps) => {
  const elementDescriptors = useMemo<ElementDescriptor[]>(() => {
    const base = exporter.getElements();
    // Charts whose title is a sibling React element (e.g. SciChart) report no
    // `title` element; surface a Title toggle from the drawer-supplied label.
    if (chartLabel && !base.some((d) => d.key === 'title')) {
      return [{ key: 'title', label: 'Title', available: true }, ...base];
    }
    return base;
  }, [exporter, chartLabel]);
  const liveSize = useMemo(() => exporter.getLiveSize(), [exporter]);

  const [options, setOptions] = useState<ExportImageOptions>(() => ({
    aspectRatio: '16:9',
    customWidth: 1600,
    customHeight: 900,
    elementSize: 'medium',
    // Visible elements start checked, missing ones start unchecked.
    elements: Object.fromEntries(
      elementDescriptors.map((d) => [d.key, d.available])
    ),
    outputFormat: supportedFormats[0] ?? 'png',
  }));

  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useHotkeys(
    'esc',
    () => {
      if (!isExporting) onClose();
    },
    { enableOnFormTags: true },
    [onClose, isExporting]
  );

  const isCustomInvalid =
    options.aspectRatio === 'custom' &&
    (!isCustomDimensionValid(options.customWidth) ||
      !isCustomDimensionValid(options.customHeight));

  // `onClose()` runs after `finally` so `setIsExporting(false)` commits
  // before the parent unmounts the Panel (would freeze the spinner state).
  const handleExport = useCallback(async () => {
    setExportError(null);

    let didDownload = false;
    try {
      const liveForFilename = exporter.getLiveSize();
      const size =
        options.aspectRatio === 'custom'
          ? {
              width: options.customWidth ?? 0,
              height: options.customHeight ?? 0,
            }
          : options.aspectRatio === 'current'
            ? (liveForFilename ?? { width: 0, height: 0 })
            : { width: 0, height: 0 };
      const filename = buildExportFilename(filenamePrefix, options, size);
      const ok = await exporter.exportAndDownload(
        { ...options, chartLabel },
        filename
      );
      if (!ok) {
        setExportError('Unable to render chart for export.');
        return;
      }
      didDownload = true;
    } catch (err) {
      setExportError(
        err instanceof Error ? err.message : 'Export failed unexpectedly.'
      );
    } finally {
      setIsExporting(false);
    }
    if (didDownload) onClose();
  }, [exporter, options, chartLabel, filenamePrefix, onClose]);

  return (
    <>
      <div className={style.header}>
        <div>
          {chartLabel && <div className={style.chartLabel}>{chartLabel}</div>}
          <h2>Export Image</h2>
        </div>
        <Button
          hasIconOnly
          size="lg"
          kind="ghost"
          onClick={onClose}
          iconDescription="Close"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          renderIcon={(props) => <Close size={20} {...props} />}
        />
      </div>
      <div className={style.body}>
        <ExportImageOptionsFields
          value={options}
          onChange={setOptions}
          elementDescriptors={elementDescriptors}
          supportedFormats={supportedFormats}
          liveSize={liveSize}
        />

        {exportError && (
          <div className={style.section} role="alert">
            {exportError}
          </div>
        )}
      </div>

      <div className={style.footer}>
        {/* Loading lives in the fixed-height footer (not appended to the
            scrolling body) so the drawer height stays uniform while exporting. */}
        {isExporting && (
          <InlineLoading
            status="active"
            description="Exporting…"
            className={style.exporting}
          />
        )}
        <Button kind="secondary" onClick={onClose} disabled={isExporting}>
          Cancel
        </Button>
        <Button
          kind="primary"
          onClick={() => {
            if (isExporting) return;
            setIsExporting(true);
            handleExport();
          }}
          disabled={isExporting || isCustomInvalid}
        >
          Export {options.outputFormat === 'jpeg' ? 'JPEG' : 'PNG'}
        </Button>
      </div>
    </>
  );
};

// Mounted once as a flex sibling to the charts; expands when open, collapses to 0 when closed.
export const ExportImageDrawer = () => {
  const stateValue = useAtomValue(exportImageStateAtom);
  const close = useSetAtom(closeExportImageAtom);
  const panelRef = useRef<HTMLElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  // Cap the panel from its sticky pinned top to the viewport bottom (measured,
  // not a hardcoded offset) so the footer stays fixed and the body scrolls.
  useEffect(() => {
    if (!stateValue.open) return;
    const measure = () => {
      const el = panelRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const BOTTOM_MARGIN = 16;
      setMaxHeight(Math.max(240, window.innerHeight - top - BOTTOM_MARGIN));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [stateValue.open]);

  return (
    <aside
      ref={panelRef}
      className={classNames(style.panel, {
        [style.panelClosed]: !stateValue.open,
      })}
      style={
        stateValue.open && maxHeight
          ? { maxHeight: `${maxHeight}px` }
          : undefined
      }
      aria-hidden={!stateValue.open}
    >
      {stateValue.open && stateValue.exporter && (
        <div className={style.inner}>
          <Panel
            exporter={stateValue.exporter}
            chartLabel={stateValue.chartLabel}
            filenamePrefix={stateValue.filenamePrefix}
            supportedFormats={stateValue.supportedFormats}
            onClose={() => close()}
          />
        </div>
      )}
    </aside>
  );
};
